import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalHeading,
    StandardModalSection,
    StandardModalText,
    Toast
} from "src/components";
import {
    getStorageAddOns,
    purchaseStorageUpgrade
} from "../api/storageApi";

const ConfirmWrap = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    text-align: center;
`;

const ConfirmIntro = styled(StandardModalText)`
    max-width: 31rem;
`;

const ConfirmQuestion = styled(StandardModalText)`
    margin-top: .25rem;
`;

const ConfirmCard = styled(PortalMetricCard)`
    align-items: center;
    max-width: 11rem;
    min-height: 15rem;
    padding: 1.5rem 1.25rem 1rem;
    text-align: center;
`;

const ConfirmAmount = styled(PortalMetricValue)`
    color: #121EB1;
`;

const ConfirmTerms = styled(StandardModalText)`
    color: #8E50A8;
    max-width: 36rem;
    font-size: 14px;
`;

export default function StorageUpgradeModal({ show, onClose, summary, onPurchased }) {
    const [options, setOptions] = useState([]);
    const [confirmOption, setConfirmOption] = useState(null);
    const [purchaseLoading, setPurchaseLoading] = useState(false);

    useEffect(() => {
        async function loadAddOns() {
            try {
                const response = await getStorageAddOns();
                if (response.length > 0) {
                    setOptions(response);
                }
            } catch {
                setOptions([]);
            }
        }

        if (show) {
            setConfirmOption(null);
            loadAddOns();
        }
    }, [show]);

    const purchaseOption = async () => {
        if (!confirmOption) return;
        setPurchaseLoading(true);
        try {
            await purchaseStorageUpgrade(confirmOption);
            Toast.info("Storage upgrade selected.");
            if (onPurchased) onPurchased();
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setPurchaseLoading(false);
        }
    };

    const renderOptionCard = (option, compact = false) => {
        const CardComponent = compact ? ConfirmCard : PortalMetricCard;
        return (
            <CardComponent
                key={option.id}
                minHeight={compact ? undefined : "16rem"}
                border={summary.currentStorage === option.storageGb ? "2px solid var(--calendar-text, #171A22)" : undefined}
            >
                {compact ? (
                    <ConfirmAmount size="1.75rem">+{option.amountLabel}</ConfirmAmount>
                ) : (
                    <>
                        <PortalMetricLabel>Storage add-on</PortalMetricLabel>
                        <PortalMetricValue size="1.75rem">{option.amountLabel}</PortalMetricValue>
                    </>
                )}
                {compact && <PortalMetricLabel color="#121EB1">Storage Add On</PortalMetricLabel>}
                <StandardModalText weight="700" color="var(--calendar-text, #171A22)">
                    {option.priceLabel}
                </StandardModalText>
                <StandardModalText size="0.8125rem">
                    {option.description}
                </StandardModalText>
                {!compact && (
                    <StandardModalActionButton
                        type="button"
                        width="100%"
                        minWidth="0"
                        $variant="primary"
                        onClick={() => setConfirmOption(option)}
                    >
                    Select {option.amountLabel}
                    </StandardModalActionButton>
                )}
            </CardComponent>
        );
    };

    return (
        <StandardModal
            open={show}
            onClose={onClose}
            widthMode="wide"
            bodyPadding="0"
            hideCloseButton
        >
            <StandardModalSection padding="2.5rem" border="0" borderRadius="0" background="transparent" gap="2rem">
                {confirmOption ? (
                    <ConfirmWrap>
                        <div>
                            <StandardModalHeading size="2rem" align="center">Confirm Storage Upgrade</StandardModalHeading>
                            <ConfirmIntro align="center">
                                You&apos;re about to upgrade your company&apos;s storage by {confirmOption.amountLabel} for {confirmOption.priceLabel}.
                            </ConfirmIntro>
                        </div>

                        <ConfirmIntro align="center">
                            Your additional storage will be applied immediately and added to your company&apos;s current subscription. The upgrade charge will be included in your next billing cycle.
                        </ConfirmIntro>

                        <ConfirmQuestion weight="700" color="var(--calendar-text, #171A22)" align="center">
                            Would you like to confirm this storage upgrade?
                        </ConfirmQuestion>

                        {renderOptionCard(confirmOption, true)}

                        <ConfirmTerms weight="600" align="center">
                            By selecting Confirm Upgrade, you authorise the storage add-on to be activated immediately and added to your company&apos;s ongoing subscription billing.
                        </ConfirmTerms>

                        <StandardModalActionGroup $center>
                            <StandardModalActionButton type="button" onClick={() => setConfirmOption(null)} disabled={purchaseLoading}>
                                Cancel
                            </StandardModalActionButton>
                            <StandardModalActionButton type="button" $variant="primary" onClick={purchaseOption} disabled={purchaseLoading}>
                                {purchaseLoading ? "Confirming..." : "Confirm"}
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    </ConfirmWrap>
                ) : (
                    <>
                        <div>
                            <StandardModalHeading size="2rem" align="center">Choose Your Storage Upgrade</StandardModalHeading>
                            <StandardModalDescription align="center" maxWidth="44rem">
                                Choose the storage capacity that matches your business needs. Your selected storage allowance replaces the included 10 GB when higher, and its fixed price is added to your main subscription billing.
                            </StandardModalDescription>
                        </div>

                        <PortalMetricGrid columns="4" gap="1rem">
                            {options.map(option => renderOptionCard(option))}
                        </PortalMetricGrid>

                        <StandardModalText align="center">
                            Your storage upgrade will be added to your current Me Business subscription and billed on your existing cycle.
                        </StandardModalText>

                        <StandardModalActionGroup $center>
                            <StandardModalActionButton type="button" onClick={onClose}>
                                Cancel
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    </>
                )}
            </StandardModalSection>
        </StandardModal>
    );
}
